import { useMutation, useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { SvbrgLanguage } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { ChatMessageProps } from '~/common/types/ChatMessage';
import { ChatStep } from '~/components/ChatStep/ChatStep';
import { Markdown } from '~/components/Markdown/Markdown';
import { useUser } from '~/hooks/useUser/useUser';
import { useChatSession } from '~/modules';
import { IntakeStepMessageInput } from '~/routes/IntakeDetail/components/IntakeMessageInput/IntakeStepMessageInput';

import { INTAKE_CHAT_REQUEST_DESCRIPTION_STEP_MUTATION } from './IntakeChatRequestDescriptionStep.mutation.gql';
import { INTAKE_CHAT_REQUEST_DESCRIPTION } from './IntakeChatRequestDescriptionStep.query.gql';

export const IntakeChatRequestDescriptionStep = ({ isCompleted, createdBy, role }: ChatMessageProps) => {
  const { t: tGlobal, i18n } = useTranslation(I18nNamespace.intake);
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeChatRequestDescriptionStep' });
  const session = useChatSession();
  const { onError } = useCustomSnackbar();

  const language = i18n.language === 'dx' ? SvbrgLanguage.DE : (i18n.language.toUpperCase() as SvbrgLanguage);

  const { data } = useQuery(INTAKE_CHAT_REQUEST_DESCRIPTION, {
    variables: session,
  });

  const [save, { loading }] = useMutation(INTAKE_CHAT_REQUEST_DESCRIPTION_STEP_MUTATION, {
    onError,
    refetchQueries: [{ query: INTAKE_CHAT_REQUEST_DESCRIPTION, variables: session }],
  });

  const description = data?.sevenbridges.chatSession.requestDescription?.text ?? '';
  const { profile } = useUser({ userId: createdBy });
  return (
    <ChatStep
      title={tGlobal('IntakeChatStep.assistant')}
      role={role}
      createdBy={createdBy}
      message={t('message', profile)}
    >
      <Box
        sx={{
          paddingTop: 2,
        }}
      >
        {data && isCompleted && <Markdown>{description}</Markdown>}
        {!isCompleted && (
          <IntakeStepMessageInput
            isLoading={loading}
            onSendMessage={(message) => {
              save({
                variables: { data: { text: message, language, chatSessionId: session.id } },
              });
            }}
          />
        )}
      </Box>
    </ChatStep>
  );
};
